import React, { useEffect, useMemo, useState } from "react";
import client, { mediaUrl } from "../lib/api";
import Layout, { PageHero } from "../components/site/Layout";
import { EmptyState } from "../components/site/common";
import { Image } from "lucide-react";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [active, setActive] = useState("All");
  useEffect(() => { client.get("/public/gallery").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoaded(true)); }, []);

  // Only show filter chips for categories that actually have published images.
  const categories = useMemo(() => {
    const set = [...new Set(items.map((g) => g.category).filter(Boolean))];
    return ["All", ...set];
  }, [items]);

  const filtered = active === "All" ? items : items.filter((g) => g.category === active);

  return (
    <Layout>
      <PageHero eyebrow="Gallery" title="Moments of service and faith." intro="Photographs from the Trust's activities and events will be published here." />
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-24">
        {loaded && items.length === 0 ? (
          <EmptyState icon={Image} title="Gallery coming soon" message="Authentic photographs of the Trust's activities and events will be added here as they become available." />
        ) : (
          <>
            {categories.length > 2 && (
              <div className="mb-12 flex flex-wrap gap-2" data-testid="gallery-filters">
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setActive(c)}
                    data-testid={`gallery-filter-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    className={`border px-4 py-2 text-xs uppercase tracking-wide transition-colors duration-300 ${
                      active === c ? "border-maroon bg-maroon text-ivory" : "border-gold/40 text-ink-muted hover:border-maroon hover:text-maroon"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}

            {filtered.length === 0 ? (
              <p className="text-sm text-ink-muted" data-testid="gallery-empty-category">No photographs in this category yet.</p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((g) => (
                  <figure key={g.id} className="group overflow-hidden border border-gold/20 bg-white" data-testid={`gallery-${g.id}`}>
                    <div className="aspect-square overflow-hidden bg-ivory">
                      <img src={mediaUrl(g.image)} alt={g.title || "Gallery image"} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    {(g.title || g.category) && (
                      <figcaption className="border-t border-gold/20 p-4">
                        {g.category && <div className="eyebrow">{g.category}</div>}
                        {g.title && <div className="mt-1 text-sm text-maroon">{g.title}</div>}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </Layout>
  );
}
